/**
 * Règles pures des commandes triangulaires (type TRI).
 * Une TRI ne contient que des lignes de qté 1 (une ligne par pièce).
 * Aucune dépendance : utilisé par useSales, OrderDetail, Orders et QtyPicker.
 */
import type { OrderLine, OrderType } from '../types'

export const TRI_TYPE = 'TRI' as const
export const TRI_DEPOT = 'J01'
export const DEFAULT_DEPOT = 'J01'

/** Éclate chaque ligne de qté N > 1 en N lignes de qté 1 (ordre conservé, champs recopiés, qté <= 0 ignorée). */
export function splitLinesToUnit(lines: OrderLine[]): OrderLine[] {
   return (lines ?? []).flatMap((a_line) => {
      const count = Math.floor(Number(a_line.qty))
      if (!(count > 0)) return []
      return Array.from({ length: count }, () => ({ ...a_line, qty: 1 }))
   })
}

/** Ajout sur une TRI : jamais de fusion, N nouvelles lignes de qté 1 à la suite. */
export function addLinesToTri(cur_lines: OrderLine[], new_lines: OrderLine[]): OrderLine[] {
   return [...cur_lines, ...splitLinesToUnit(new_lines)]
}

/**
 * Modification d'une ligne de TRI, identifiée par sa position (numéro = index + 1).
 * new_line.sku est le SKU final (éventuellement nouveau), new_line.qty la quantité saisie.
 * - qté <= 0 : la ligne est supprimée
 * - sinon la ligne garde sa position, prend le SKU (et prix si fourni) et reste en qté 1 ;
 *   N - 1 lignes de qté 1 (même SKU, même prix) sont ajoutées à la suite.
 * Les autres lignes, même de même SKU, ne sont jamais touchées.
 */
export function updateTriLineAtPosition(cur_lines: OrderLine[], line_num: number, new_line: OrderLine): OrderLine[] {
   const index = line_num - 1
   if (!Number.isInteger(index) || index < 0 || index >= cur_lines.length) {
      return addLinesToTri(cur_lines, [new_line])
   }
   const qty = Math.floor(Number(new_line.qty))
   if (!(qty > 0)) {
      return cur_lines.filter((_, i) => i !== index)
   }
   const old_line = cur_lines[index]
   const edited: OrderLine = { ...old_line, sku: new_line.sku, qty: 1 }
   if (new_line.price !== undefined) edited.price = new_line.price
   const price = edited.price
   const extra = Array.from({ length: qty - 1 }, () => {
      const l: OrderLine = { sku: new_line.sku, qty: 1 }
      if (price !== undefined) l.price = price
      return l
   })
   return [...cur_lines.slice(0, index), edited, ...cur_lines.slice(index + 1), ...extra]
}

/** Conversion de type autorisée ? CDE/CDI -> TRI oui, CON/DEV (et autres) -> TRI non, TRI -> autre oui. */
export function canConvertOrderType(from: OrderType | string | null | undefined, to: OrderType | string): boolean {
   if (to !== TRI_TYPE) return true
   return from === TRI_TYPE || from === 'CDE' || from === 'CDI'
}

/** Lignes après conversion de type : éclatement quand on devient TRI, inchangées sinon. */
export function convertLinesForType(from: OrderType | string | null | undefined, to: OrderType | string, lines: OrderLine[]): OrderLine[] {
   if (to === TRI_TYPE && from !== TRI_TYPE) return splitLinesToUnit(lines)
   return lines
}

/** Dépôt interrogé pour la disponibilité : TRI -> toujours J01, sinon dépôt client, à défaut J01. */
export function getAvailabilityDepot(order_type: OrderType | string | null | undefined, customer_depot: string | null | undefined): string {
   if (order_type === TRI_TYPE) return TRI_DEPOT
   return customer_depot || DEFAULT_DEPOT
}
